import React, { useState } from 'react';
import { ChevronDown, ChevronUp, FileText, Pencil, Check, X } from 'lucide-react';
import SessionsPanel from './SessionsPanel';

/**
 * WhiteboardSidebar
 *
 * Left sidebar for the whiteboard page containing:
 * - SessionsPanel (student sessions with generate buttons)
 * - Whiteboards list (collapsible)
 * - Quick Tips footer
 */
const WhiteboardSidebar = ({
  isOpen = false,
  onClose,
  whiteboards,
  selectedId,
  onSelect,
  onNew,
  onRename,
  onDelete,
  // Sessions panel props
  onGenerateContent,
  isGenerating,
  generatingSessionId
}) => {
  const [whiteboardsExpanded, setWhiteboardsExpanded] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const beginRename = (whiteboard) => {
    setEditingId(whiteboard.id);
    setEditValue(whiteboard.title || '');
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditValue('');
    setIsRenaming(false);
  };

  const submitRename = async (id) => {
    const nextTitle = editValue.trim();
    if (!nextTitle || !onRename) {
      cancelRename();
      return;
    }
    setIsRenaming(true);
    const success = await onRename(id, nextTitle);
    if (success) {
      cancelRename();
    } else {
      setIsRenaming(false);
    }
  };

  return (
    <aside className={`whiteboard-sidebar ${isOpen ? 'open' : ''}`}>
      <div className="whiteboard-sidebar-mobile-header">
        <h3>Workspace</h3>
        <button
          type="button"
          className="whiteboard-sidebar-mobile-close"
          onClick={onClose}
          aria-label="Close whiteboard sidebar"
        >
          Close
        </button>
      </div>
      {/* Sessions Panel - New Section */}
      <SessionsPanel
        onGenerateContent={onGenerateContent}
        isGenerating={isGenerating}
        generatingSessionId={generatingSessionId}
      />

      {/* Whiteboards Section - Now Collapsible */}
      <div className="whiteboard-sidebar-section">
        <div
          className="whiteboard-sidebar-section__header"
        >
          <button
            type="button"
            className="whiteboard-sidebar-section__toggle"
            onClick={() => setWhiteboardsExpanded(!whiteboardsExpanded)}
            aria-expanded={whiteboardsExpanded}
          >
            <FileText size={14} />
            <span>My Whiteboards</span>
            {whiteboardsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <div className="whiteboard-sidebar-section__actions">
            <button
              type="button"
              className="lrbrain-btn lrbrain-btn-solid whiteboard-new-btn"
              onClick={onNew}
            >
              + New
            </button>
          </div>
        </div>

        <div className={`whiteboard-sidebar-section__content ${!whiteboardsExpanded ? 'whiteboard-sidebar-section__content--collapsed' : ''}`}>
          <div className="whiteboard-sidebar-list">
            {whiteboards.length === 0 ? (
              <div className="whiteboard-empty-state">
                <div className="whiteboard-empty-icon">📝</div>
                <p>No saved whiteboards yet</p>
                <p className="whiteboard-empty-hint">
                  Click "New" to create your first whiteboard
                </p>
              </div>
            ) : (
              whiteboards.map((wb) => (
                <div
                  key={wb.id}
                  className={`whiteboard-sidebar-item ${selectedId === wb.id ? 'selected' : ''}`}
                  onClick={() => {
                    if (editingId !== wb.id) {
                      onSelect(wb.id);
                    }
                  }}
                >
                  <div className="whiteboard-item-content">
                    {editingId === wb.id ? (
                      <input
                        className="whiteboard-item-title-input"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            submitRename(wb.id);
                          }
                          if (e.key === 'Escape') {
                            e.preventDefault();
                            cancelRename();
                          }
                        }}
                        disabled={isRenaming}
                        autoFocus
                      />
                    ) : (
                      <div className="whiteboard-item-title">{wb.title || 'Untitled'}</div>
                    )}
                    <div className="whiteboard-item-date">{formatDate(wb.updated_at)}</div>
                  </div>
                  {editingId === wb.id ? (
                    <div className="whiteboard-item-actions">
                      <button
                        className="whiteboard-item-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          submitRename(wb.id);
                        }}
                        title="Save name"
                        disabled={isRenaming}
                      >
                        <Check size={14} />
                      </button>
                      <button
                        className="whiteboard-item-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          cancelRename();
                        }}
                        title="Cancel rename"
                        disabled={isRenaming}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="whiteboard-item-actions">
                      <button
                        className="whiteboard-item-action"
                        onClick={(e) => {
                          e.stopPropagation();
                          beginRename(wb);
                        }}
                        title="Rename whiteboard"
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  )}
                  <button
                    className="whiteboard-item-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(wb.id);
                    }}
                    title="Delete whiteboard"
                  >
                    ×
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Tips Footer */}
      <div className="whiteboard-sidebar-footer">
        <div className="whiteboard-tips">
          <h4>Quick Tips</h4>
          <ul>
            <li><strong>V</strong> - Select mode</li>
            <li><strong>P/D</strong> - Pen tool</li>
            <li><strong>H</strong> - Highlighter</li>
            <li><strong>E</strong> - Eraser</li>
            <li><strong>N</strong> - Add note</li>
            <li><strong>M</strong> - Add math</li>
            <li><strong>G</strong> - Add graph</li>
          </ul>
        </div>
      </div>
    </aside>
  );
};

export default WhiteboardSidebar;
